import { type AnalyticsColumnResult } from "./analyticsColumnResult";
import { CheckResult, ProcedureColumn } from "./auditSession";
import { CONTROL_EXCEPTION_DESCRIPTIONS } from "./attributeTestColumn";
import { AnalyticsResultSummary } from "./procedureSessionTableShell";

/**
 * Translation layer between an analytics verdict and the `CheckResult` shape the
 * grid's `CheckDecision` renders, so an analytics cell speaks the one badge
 * vocabulary the workpaper already has.
 */

/** The namespace an analytics cell's `CheckResult` id carries. */
const ID_PREFIX = "analytics-id-";

/**
 * The verdict id behind an analytics cell's selection id, or `undefined` when the
 * selection is not an analytics cell's — which includes the bare prefix, naming
 * no verdict. What comes back is a non-empty remainder, or nothing.
 *
 * The namespace exists because one selection slot carries every cell kind's id,
 * so the prefix is what tells the detail panels apart. Reading it lives here
 * beside the function that writes it — a second copy of the literal is how a
 * cell selects but opens nothing.
 */
export function analyticsResultIdFromSelection(
  checkResultId: string | undefined,
): string | undefined {
  if (!checkResultId?.startsWith(ID_PREFIX)) return undefined;
  const resultId = checkResultId.substring(ID_PREFIX.length);
  return resultId === "" ? undefined : resultId;
}

/**
 * Whether a selection id is in the analytics namespace, verdict id behind it or
 * not. True on exactly one id more than `analyticsResultIdFromSelection` is
 * truthy on: the bare prefix, which is in the namespace and names no verdict.
 *
 * That one id is why the two questions are separate. A dispatch choosing which
 * detail surface owns a selection has to claim the namespace first, or the
 * bare prefix falls through to a surface that reads a different id space and
 * says it found nothing; what to render once claimed is then the verdict id's
 * question, asked inside the surface that owns it.
 */
export function selectionIsInAnalyticsNamespace(
  checkResultId: string | undefined,
): boolean {
  return checkResultId?.startsWith(ID_PREFIX) ?? false;
}

/**
 * The verdict fields a cell's badge is derived from.
 *
 * Declared as the subset rather than as either wire shape because both feed it:
 * the grid passes the slim `AnalyticsResultSummary` and the detail panel passes
 * the full verdict from `useAnalyticsResultDetail`. One derivation over one
 * subset is what keeps the badge from changing as the panel opens over the cell
 * the auditor just read.
 *
 * `analyticsColumnId` earns its place structurally, not by derivation: it is the
 * one field only an analytics verdict has, so it stops an `AttributeTestResultSummary`
 * (whose column id is `attributeTestColumnId`) from satisfying this subset and
 * being minted into an `analytics-id-` selection that opens onto a 404.
 */
export type AnalyticsVerdictBadgeFields = Pick<
  AnalyticsResultSummary,
  | "id"
  | "analyticsColumnId"
  | "isConclusive"
  | "outcome"
  | "controlExceptions"
  | "reviewerType"
  | "userId"
  | "lockedBy"
  | "columnVersion"
  | "runId"
  | "createdAt"
  | "updatedAt"
>;

/**
 * Which of the three decisions the verdict amounts to.
 *
 * An inconclusive verdict is a failure to reach an answer, not a pass, so it maps
 * to `fail` — as does a conclusive verdict whose `outcome` the projection did not
 * send, because a verdict that cannot state its outcome has not stated a pass.
 */
function decisionFor(
  summary: AnalyticsVerdictBadgeFields,
): CheckResult["decision"] {
  if (!summary.isConclusive) return "fail";
  if (summary.outcome === "pass") return "pass";
  if (summary.outcome === "not_applicable") return "not_applicable";
  return "fail";
}

/**
 * The issue list whose display status is the verdict's status.
 *
 * `CheckDecision` derives the badge from `getIssueDisplayStatus(issues)` and
 * never from `decision`, so this list — not the decision above — is what the
 * auditor reads. The verdict alone decides the status: every branch ends in a
 * marker naming it, and the exceptions ride along beside the marker.
 */
function issuesFor(
  summary: AnalyticsVerdictBadgeFields,
): CheckResult["issues"] {
  const exceptions: CheckResult["issues"] = summary.controlExceptions.map(
    (exception) => ({
      issueCategory: "exception",
      issueSubcategory: exception,
      description: CONTROL_EXCEPTION_DESCRIPTIONS[exception] || exception,
    }),
  );

  // The marker carries the verdict's status, not its cause. It names one reason
  // because that is the only member the shared subcategory vocabulary holds for
  // an inconclusive verdict, and the badge resolves it to "Requires Attention"
  // through that name; the reasons the pipeline actually gave are the pane's to
  // list, read off the full verdict.
  //
  // What that costs: the name asserts a specific cause this list never verified,
  // and the two surfaces are sourced differently for one fact. They cannot
  // disagree about status, which is what the auditor acts on — every
  // inconclusive verdict badges amber here whatever reasons it carries.
  //
  // Deriving the marker from the reasons instead is rejected, not untried. The
  // grid projection does not carry them, so the badge and the pane would derive
  // from different data and the badge could change as the pane opens over it.
  // Worse, an empty reason list is the ordinary case rather than a corner — it
  // is what the wire sends when a verdict names none — so a list-derived marker
  // with no unconditional floor yields no issues at all, and an empty issue list
  // badges green. A failing verdict reported as a clean pass is the one outcome
  // that outranks everything else here.
  if (!summary.isConclusive) {
    return [
      ...exceptions,
      {
        issueCategory: "test_outcome",
        issueSubcategory: "exhausted_search",
        description: "Testing could not be completed",
      },
    ];
  }

  if (summary.outcome === "not_applicable") {
    return [
      ...exceptions,
      {
        issueCategory: "test_outcome",
        issueSubcategory: "not_applicable",
        description: "Not applicable",
      },
    ];
  }

  // A pass badges as a pass whatever its exception list holds. Exceptions on a
  // pass are ones the criteria tolerate ("pass if fewer than 10 exceptions"),
  // so the badge states Midship's verdict and the conclusion says what was
  // found (MS-3733). Without the marker the exception category alone would
  // read as issues found — a red badge on a passing cell whose pane says Pass.
  if (summary.outcome === "pass") {
    return [
      ...exceptions,
      {
        issueCategory: "test_outcome",
        issueSubcategory: "no_issues_found",
        description: "No issues found",
      },
    ];
  }

  // A failing verdict. The marker is unconditional because a failure that
  // enumerated no exceptions is still a failure, and an empty list renders as
  // "No Issues Found" — a failing cell reported clean is the worst thing this
  // badge can say.
  return [
    ...exceptions,
    {
      issueCategory: "test_outcome",
      issueSubcategory: "issues_found",
      description: "Issues found",
    },
  ];
}

/**
 * Cell-badge translator over the fields both wire shapes carry, so an analytics
 * cell renders from `DrySessionSourceRow`'s slim projection without a per-row
 * `useSourceRow` fanout and the detail panel badges the same verdict identically.
 * Heavy fields (`reason`, `citations`) are empty by construction — neither is on
 * the badge subset; the detail panel fills the prose it fetched.
 *
 * Shaped like `attributeTestSummaryToCheckResult`, but deliberately not sharing
 * its status derivation: that one emits its `no_issues_found` marker only for a
 * `pass`, which leaves a `fail` carrying no exceptions with an empty issue list
 * and a "No Issues Found" badge. The status here comes from the verdict:
 *
 *   inconclusive              → requires_attention
 *   fail                      → issues_found, whatever the exception list holds
 *   not_applicable            → na
 *   pass                      → no_issues, whatever the exception list holds
 *                               (a pass within the criteria's tolerance)
 *
 * A cell holding no verdict has no row in that table: it never reaches this
 * function, and `CheckDecision` renders an empty cell without consulting a
 * status. That exclusion is deliberate and cannot be relaxed —
 * `getIssueDisplayStatus` derives from `issues[]` alone and an empty array is
 * `no_issues`, so no issue list can express "no verdict"; routing that state
 * through here badges an ungenerated cell green. A later unification of the two
 * derivations has to keep it outside the issues array.
 */
export function analyticsSummaryToCheckResult(
  summary: AnalyticsVerdictBadgeFields,
  column: ProcedureColumn,
): CheckResult {
  return {
    id: `${ID_PREFIX}${summary.id}`,
    checkId: null,
    // The procedure column, not the analytics column: `CheckDecision` resolves a
    // rerun target by matching this against `procedure.columns[*].id`.
    evalColumnId: column.id,
    reviewerType: summary.reviewerType,
    userId: summary.userId ?? null,
    decision: decisionFor(summary),
    reason: "",
    // A cell holds at most one live analytics verdict — the projection sends live
    // rows only and a uniqueness constraint admits one — so there is no review
    // sequence to hold a position in.
    reviewOrder: 0,
    lockedBy: summary.lockedBy ?? null,
    issues: issuesFor(summary),
    citations: [],
    columnVersion: summary.columnVersion,
    // The Midship `session_run`, never `runManifestId`: that names an AuditBoard
    // workflow run, while every consumer of this field compares it against a
    // `session_run` id, and a cell answering a different question with it would
    // suppress the error badge for a run that genuinely failed. The full verdict
    // the detail panel passes lands here as null: the wire carries a run id on
    // it, but the pane's schema does not declare one and zod strips what it does
    // not declare. The panel badges an already-open cell, so it has no error
    // badge to suppress.
    runId: summary.runId ?? null,
    createdAt: summary.createdAt,
    updatedAt: summary.updatedAt,
  };
}

/**
 * The text that states an analytics verdict: its outcome text when it
 * concluded, otherwise its account of the gap.
 *
 * An inconclusive verdict's outcome text can describe a pass that was
 * withdrawn, so it stands in only when the verdict has no account of the gap.
 * Any withdrawal records that account, so the fallback cannot bring a withdrawn
 * pass back.
 */
export function analyticsVerdictText(
  result: Pick<
    AnalyticsColumnResult,
    "isConclusive" | "outcomeDescription" | "partialDescription"
  >,
): string | null {
  if (result.isConclusive) return result.outcomeDescription;
  return result.partialDescription?.trim()
    ? result.partialDescription
    : result.outcomeDescription;
}

/**
 * An analytics verdict's conclusion prose — the auditor trail's conclusion line
 * when the writer composed one, otherwise the verdict's own summary of itself.
 *
 * The trail leads because it is the composed line: a judged verdict carries one
 * while `evidenceSummary` stays null, so reading the summary alone leaves every
 * such conclusion blank on screen while the text sits in the payload. The
 * summary still answers for a verdict written before the trail carried a
 * conclusion line, and `partialDescription` for one that never concluded.
 */
export function analyticsConclusionProse(
  result: Pick<
    AnalyticsColumnResult,
    "isConclusive" | "evidenceSummary" | "partialDescription" | "auditorTrail"
  >,
): string | null {
  if (result.auditorTrail?.l1.trim()) return result.auditorTrail.l1;
  return result.isConclusive
    ? result.evidenceSummary
    : result.partialDescription;
}
